import React from "react"
import { ui } from "../../../shared/styles/uiSystem"
import PillTabs from "../../../shared/components/PillTabs"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusBanner from "../../../shared/components/StatusBanner"
import StatusPill from "../../../shared/components/StatusPill"

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function resolvePatientStatus(patient) {
  const pendingBalance = Number(patient.pendingBalance || patient.balance || 0)
  const activePlans = Number(patient.activePlans || 0)

  if (pendingBalance > 0) {
    return {
      label: "Debe",
      tone: "danger"
    }
  }

  if (activePlans > 0) {
    return {
      label: "Plan activo",
      tone: "info"
    }
  }

  return {
    label: "Al corriente",
    tone: "success"
  }
}

function PatientMetaPill({ label, value, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
        : { bg: "#f8fafc", border: "#e2e8f0", color: "#334155" }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        minHeight: 24,
        borderRadius: 999,
        padding: "0 8px",
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 11,
        fontWeight: 850,
        whiteSpace: "nowrap"
      }}
    >
      <span style={{ color: "#64748b", fontWeight: 800 }}>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function PatientListCard({ patient, isSelected, onClick }) {
  const fullName = `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
  const pendingBalance = Number(patient.pendingBalance || patient.balance || 0)
  const activePlans = Number(patient.activePlans || 0)
  const status = resolvePatientStatus(patient)
  const contactLine = [patient.phone, patient.email].filter(Boolean).join(" · ")

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: 11,
        marginBottom: 8,
        position: "relative",
        overflow: "hidden",
        borderRadius: 15,
        border: isSelected ? "1px solid #93c5fd" : "1px solid #e2e8f0",
        background: isSelected ? "#f8fbff" : "#ffffff",
        boxShadow: isSelected
          ? "0 10px 18px rgba(37,99,235,0.09)"
          : "0 4px 12px rgba(15,23,42,0.035)",
        cursor: "pointer"
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: 3,
          background: isSelected ? "#2563eb" : "transparent"
        }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "flex-start",
          minWidth: 0
        }}
      >
        <div
          style={{
            minWidth: 0,
            fontWeight: 900,
            color: "#0f172a",
            lineHeight: 1.15,
            fontSize: 14.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
          title={fullName || "Paciente"}
        >
          {fullName || "Paciente"}
        </div>

        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          marginTop: 5,
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
        title={contactLine || "Sin datos de contacto"}
      >
        {contactLine || "Sin datos de contacto"}
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          marginTop: 9
        }}
      >
        <PatientMetaPill
          label="Pendiente"
          value={formatMoney(pendingBalance)}
          tone={pendingBalance > 0 ? "danger" : "neutral"}
        />

        <PatientMetaPill
          label="Planes"
          value={String(activePlans)}
          tone={activePlans > 0 ? "info" : "neutral"}
        />
      </div>
    </button>
  )
}

export default function PatientsListPanel({
  filteredPatients,
  selectedPatientId,
  loadingPatients,
  isClinicSelectionBlocked,
  isReadOnly,
  query,
  setQuery,
  activeFilter,
  setActiveFilter,
  filters,
  focusFromUrl,
  onCreatePatient,
  onOpenPatient,
  compact = false
}) {
  const filterItems = filters.map((filter) => ({
    value: filter.id,
    label: filter.label
  }))

  const recallMode =
    String(focusFromUrl || "").trim().toUpperCase() === "RECALL"

  return (
    <div
      style={{
        ...ui.card,
        minHeight: compact ? "auto" : 460,
        display: compact ? "grid" : "flex",
        flexDirection: compact ? undefined : "column",
        overflow: "hidden",
        minWidth: 0
      }}
    >
      <div
        style={{
          padding: compact ? 10 : 11,
          borderBottom: "1px solid #e2e8f0",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          display: "grid",
          gap: compact ? 8 : 9
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap"
          }}
        >
          <div>
            <div style={{ ...ui.sectionTitle, fontSize: 16 }}>Listado</div>
            <div style={ui.metaText}>
              {filteredPatients.length} visibles
            </div>
          </div>

          <button
            disabled={isReadOnly}
            onClick={onCreatePatient}
            style={{
              ...ui.primaryButton,
              minHeight: 32,
              padding: "0 12px",
              opacity: isReadOnly ? 0.7 : 1,
              cursor: isReadOnly ? "not-allowed" : "pointer"
            }}
          >
            Nuevo
          </button>
        </div>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar paciente"
          style={{
            ...ui.input,
            height: 36,
            fontSize: 12.5
          }}
        />

        {recallMode ? (
          <StatusBanner
            tone="info"
            title="Vista desde dashboard"
            text="Pacientes con foco en recall o promoción."
          />
        ) : null}

        <PillTabs
          items={filterItems}
          value={activeFilter}
          onChange={setActiveFilter}
          compact
        />
      </div>

      <div
        style={{
          padding: compact ? 8 : 9,
          overflowY: compact ? "visible" : "auto",
          flex: compact ? undefined : 1,
          background: "#f8fafc"
        }}
      >
        {isClinicSelectionBlocked ? (
          <EmptyStateBlock
            title="Selecciona una clínica"
            text="Primero elige una clínica para cargar la lista."
            compact
          />
        ) : loadingPatients ? (
          <EmptyStateBlock
            title="Cargando pacientes"
            text="Estamos preparando la base visible."
            compact
          />
        ) : !filteredPatients.length ? (
          <EmptyStateBlock
            title="Sin resultados"
            text="No encontramos pacientes con esos filtros."
            compact
          />
        ) : (
          filteredPatients.map((patient) => (
            <PatientListCard
              key={patient.id}
              patient={patient}
              isSelected={String(selectedPatientId || "") === String(patient.id)}
              onClick={() => onOpenPatient(patient.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}
